import { z } from "zod";

// ---------------------------------------------------------------------------
// 言葉の切替（縦串8、013＋027、docs/22）。射場さん 9/17 の決定：
//   「得意先」は「顧客」に。ただし会社ごとに切り替えられるようにする（工事名⇄件名と同じ枠）。
//
// 画面・紙・文の中の項目名は、この 24 のキーで引く（#142 段3 で trade・schedule・vendorQuote・takeoff、段5・段6 で siteAddress・task・contractor・siteShort・takeoffSheet、#213 で referrer、
// #222 で明細の列の見出し lineName・lineSpec・lineNote、#233 で帳票の名前 order・orderAck、3b で paymentNotice を足した）。既定は DB の vocabulary_presets（工務店向け／一般の会社向け）、
// 会社ごとの言い換えは tenants.vocabulary_overrides。解決した言葉（labels）は Me.tenant.labels と Company.labels に乗る。
// 画面の決まり文（*_MSG）の中では {customer} {subject} のように書き、見せるときに fillLabels() で置き換える。
// ---------------------------------------------------------------------------

export const VOCAB_KEYS = [
  "customer",
  "subject",
  "site",
  "period",
  "outline",
  "project",
  "job",
  "vendor",
  "trade",
  "schedule",
  "vendorQuote",
  "takeoff",
  "siteAddress",
  "task",
  "contractor",
  "siteShort",
  "takeoffSheet",
  "referrer",
  "lineName",
  "lineSpec",
  "lineNote",
  "order",
  "orderAck",
  "paymentNotice",
  "deliveryNote",
  "acceptance",
  "ongoingProject",
] as const;
export const VocabKey = z.enum(VOCAB_KEYS);
export type VocabKey = z.infer<typeof VocabKey>;

/** 業種の既定（DB の vocabulary_presets と同じ2つ） */
export const VocabularyPreset = z.enum(["construction", "general"]);
export type VocabularyPreset = z.infer<typeof VocabularyPreset>;
export const VOCAB_PRESET_LABEL: Record<VocabularyPreset, string> = {
  construction: "工務店・職人向け（工事名・工事場所・工期・工事概要）",
  general: "一般の会社向け（件名・納品先・納期・概要）",
};

export const Labels = z.object({
  customer: z.string(),
  subject: z.string(),
  site: z.string(),
  period: z.string(),
  outline: z.string(),
  project: z.string(),
  job: z.string(),
  /** 見積をもらう相手（047、docs/33）：外注先／協力会社／仕入先 */
  vendor: z.string(),
  /** 行の区分（#129。#142 段3）：工種／分類 */
  trade: z.string(),
  /** 工程管理（#100。#142 段3）：工程表／スケジュール */
  schedule: z.string(),
  /** 相手からもらう見積（#81・#84。#142 段3）：外注見積／仕入先見積 */
  vendorQuote: z.string(),
  /** 数量の根拠（#130。#142 段3）：拾い出し／数量の内訳 */
  takeoff: z.string(),
  /** 案件の住所（#142 段6）：現場住所／納品先住所（#280 で「納品先の住所」から） */
  siteAddress: z.string(),
  /** 工程表の行（#142 段5）：工程／作業 */
  task: z.string(),
  /** 工程表の担当の相手（#142 段5）：協力会社／外注先。「業者」の文もこのキー */
  contractor: z.string(),
  /** 工程表の中の案件（#142 段5）：現場／案件（「n現場」「現場別」「現場全体」） */
  siteShort: z.string(),
  /** 拾い出しの別紙（#142 段6）：積算表／数量の内訳表 */
  takeoffSheet: z.string(),
  /** 紹介手数料を払う相手（#213、docs/40）：パートナー（紹介者・紹介元などに言い換えられる） */
  referrer: z.string(),
  /** 明細の列の見出し（#222、docs/22 §6）：名称（品名・商品名・品目・項目…）。紙・画面の明細の表・CSV の見出し */
  lineName: z.string(),
  /** 明細の列の見出し（#222）：規格（品番・型番・仕様・サイズ・内容…） */
  lineSpec: z.string(),
  /** 明細の列の見出し（#222）：備考（摘要）。紙の下の「備考」の枠（帳票の備考）とは別 */
  lineNote: z.string(),
  /** {vendor}に出す帳票の名前（#233、docs/93 §1）：注文書／発注書・工事注文書… 設定 → {order}「名称」で変える（言い換えの一覧には出さない） */
  order: z.string(),
  /** その請書の名前（#233）：注文請書／発注請書。帳票の名前を選ぶと対の名前が入る */
  orderAck: z.string(),
  /** {vendor}に渡す仕入明細書の名前（#233 3b、docs/93 §15.6）：支払通知書／支払明細書。設定 → {order}「名称」で変える */
  paymentNotice: z.string(),
  /** {customer}に品物と一緒に渡す帳票の名前（#266、docs/96）：納品書（出荷案内書・納品書兼受領書…）。設定 → 言葉の言い換え */
  deliveryNote: z.string(),
  /** {customer}が受け取り・検査したことの帳票の名前（#266。1b で使う）：工務店向け 完了報告書／一般 検収書（物販・卸と食品は 物品受領書） */
  acceptance: z.string(),
  /** 継続案件の呼び方（#289、docs/101 §8.2）：継続案件／現場／契約／取引先契約。6文字まで。業種で初期値（卸売＝現場・物販＝取引先契約・IT・士業・保守＝契約） */
  ongoingProject: z.string(),
});
export type Labels = z.infer<typeof Labels>;

/** 工務店向けの既定（DB の vocabulary_presets の construction と同じ）。Me を読む前の画面や、テストの期待値に */
export const DEFAULT_LABELS: Labels = {
  customer: "顧客",
  subject: "工事名",
  site: "工事場所",
  period: "工期",
  outline: "工事概要",
  project: "案件",
  job: "工事",
  vendor: "外注先",
  trade: "工種",
  schedule: "工程表",
  vendorQuote: "外注見積",
  takeoff: "拾い出し",
  siteAddress: "現場住所",
  task: "工程",
  contractor: "協力会社",
  siteShort: "現場",
  takeoffSheet: "積算表",
  referrer: "パートナー",
  lineName: "名称",
  lineSpec: "規格",
  lineNote: "備考",
  order: "注文書",
  orderAck: "注文請書",
  paymentNotice: "支払通知書",
  deliveryNote: "納品書",
  acceptance: "完了報告書",
  ongoingProject: "継続案件",
};

/**
 * 2つの既定（DB の vocabulary_presets と同じ。会社の言い換えの無い言葉）。ログイン前の文・公開のヘルプ（#150、docs/59）が製品の既定で埋めるときに使う。
 * DB と食い違えば apps/api/test/help.test.ts ①が落とす
 */
export const PRESET_LABELS: Record<VocabularyPreset, Labels> = {
  construction: DEFAULT_LABELS,
  general: {
    customer: "顧客",
    subject: "件名",
    site: "納品先",
    period: "納期",
    outline: "概要",
    project: "案件",
    job: "分",
    vendor: "仕入先",
    trade: "分類",
    schedule: "スケジュール",
    vendorQuote: "仕入先見積",
    takeoff: "数量の内訳",
    siteAddress: "納品先住所",
    task: "作業",
    contractor: "外注先",
    siteShort: "案件",
    takeoffSheet: "数量の内訳表",
    referrer: "パートナー",
    lineName: "名称",
    lineSpec: "規格",
    lineNote: "備考",
    order: "発注書",
    orderAck: "発注請書",
    paymentNotice: "支払通知書",
    deliveryNote: "納品書",
    acceptance: "検収書",
    ongoingProject: "継続案件",
  },
};

/**
 * 設定の画面の説明：何の言葉か・どこに出るか・よく使う言い換え（候補は製品ごと。#142 段6 No.19）。
 * title・where の {customer} {pw:projectExample} などは画面が t() で埋める
 */
export const VOCAB_KEY_INFO: Record<VocabKey, { title: string; where: string; suggestions: { genba: string[]; eigyo: string[] } }> = {
  customer: { title: "見積を出す相手", where: "メニュー、一覧の列、「〜に出す」のボタン", suggestions: { genba: ["顧客", "得意先", "お客様", "施主", "取引先", "元請"], eigyo: ["顧客", "取引先", "お客様", "クライアント"] } },
  subject: { title: "見積の題（何の見積か）", where: "見積書・請求書の紙の見出し、一覧の列", suggestions: { genba: ["工事名", "件名", "品名"], eigyo: ["件名", "品名"] } },
  site: { title: "仕事をする場所", where: "紙の見出し（詳細）", suggestions: { genba: ["工事場所", "現場", "納品先", "施工場所"], eigyo: ["納品先", "設置先", "会場"] } },
  period: { title: "いつからいつまで", where: "紙の見出し（詳細）", suggestions: { genba: ["工期", "納期", "施工期間"], eigyo: ["納期", "実施日", "公開日"] } },
  outline: { title: "仕事のあらまし", where: "紙の見出し（詳細）", suggestions: { genba: ["工事概要", "概要", "作業内容"], eigyo: ["概要", "仕様"] } },
  project: { title: "見積と請求書をまとめる箱（{pw:projectExample}）", where: "メニュー、{project}の一覧、見積の紙の上", suggestions: { genba: ["案件", "現場", "物件"], eigyo: ["案件", "プロジェクト"] } },
  job: { title: "追加見積の言葉", where: "2枚目からの見積の名前の既定（「〇〇（追加{job}）」）", suggestions: { genba: ["工事", "作業", "分"], eigyo: ["分", "回"] } },
  vendor: { title: "見積をもらう相手（仕入れの相手）", where: "{customer}の一覧の切り替え、{vendorQuote}の画面", suggestions: { genba: ["外注先", "協力会社", "仕入先", "業者"], eigyo: ["仕入先", "外注先", "パートナー"] } },
  trade: { title: "行の区分", where: "見積の行の詳細、並べ方、設定の区分", suggestions: { genba: ["工種", "分類", "工事種別", "費目"], eigyo: ["分類", "種別", "カテゴリ"] } },
  schedule: { title: "日程の表", where: "メニュー、{project}のタブ、設定", suggestions: { genba: ["工程表", "スケジュール", "日程表"], eigyo: ["スケジュール", "進行表"] } },
  vendorQuote: { title: "相手からもらう見積", where: "{project}のタブ、見積の帯、ホーム", suggestions: { genba: ["外注見積", "仕入先見積", "業者見積"], eigyo: ["仕入先見積", "外注見積"] } },
  takeoff: { title: "数量の根拠", where: "見積の数量の欄、{takeoffSheet}", suggestions: { genba: ["拾い出し", "数量の内訳", "数量計算"], eigyo: ["数量の内訳", "計算書"] } },
  siteAddress: { title: "{project}の住所", where: "{project}の画面、新しい{project}、持ち出しの CSV", suggestions: { genba: ["現場住所", "工事場所の住所"], eigyo: ["納品先住所", "設置先住所", "会場住所"] } },
  task: { title: "{schedule}の行", where: "{schedule}の行・紙・協力会社に送るページ", suggestions: { genba: ["工程", "作業"], eigyo: ["作業", "タスク", "工程"] } },
  contractor: { title: "{schedule}の担当の相手", where: "{schedule}の担当・「〜別」・〜からの報告", suggestions: { genba: ["協力会社", "業者", "外注先"], eigyo: ["外注先", "パートナー", "協力会社"] } },
  siteShort: { title: "{schedule}の中で{project}を呼ぶ言葉", where: "全体の{schedule}の「n件」「〜別」「〜全体」", suggestions: { genba: ["現場", "物件"], eigyo: ["案件", "プロジェクト"] } },
  takeoffSheet: { title: "{takeoff}の別紙", where: "見積書の PDF の最後の別紙、設定", suggestions: { genba: ["積算表", "数量計算書"], eigyo: ["数量の内訳表", "計算書"] } },
  referrer: { title: "紹介手数料を払う相手", where: "{customer}の詳細と一覧、見積の詳細と社内用の列、設定", suggestions: { genba: ["パートナー", "紹介者", "紹介元"], eigyo: ["パートナー", "紹介者", "代理店"] } },
  // 明細の列（#222）：設定の節「明細の列」で変える（言い換えの一覧には出さない＝WORD_KEYS）。候補は両製品で同じ
  lineName: { title: "名称", where: "明細の表の見出し（紙・画面・CSV）", suggestions: { genba: ["名称", "品名", "商品名", "品目", "項目"], eigyo: ["名称", "品名", "商品名", "品目", "項目"] } },
  lineSpec: { title: "規格", where: "明細の表の見出し（紙・画面・CSV）", suggestions: { genba: ["規格", "品番", "型番", "仕様", "サイズ", "内容"], eigyo: ["規格", "品番", "型番", "仕様", "サイズ", "内容"] } },
  lineNote: { title: "備考", where: "明細の表の見出し（紙・画面・CSV）", suggestions: { genba: ["備考", "摘要"], eigyo: ["備考", "摘要"] } },
  // 帳票の名前（#233）：設定 → {order}「名称」で変える（言い換えの一覧には出さない＝WORD_KEYS）
  order: { title: "{vendor}に出す帳票", where: "メニュー、{order}の紙の題、メール、承諾ページ", suggestions: { genba: ["注文書", "発注書", "工事注文書", "外注発注書"], eigyo: ["発注書", "注文書", "申込書"] } },
  orderAck: { title: "{order}の請書", where: "承諾したあとの紙の題、承諾ページ", suggestions: { genba: ["注文請書", "発注請書", "工事注文請書"], eigyo: ["発注請書", "注文請書", "申込承諾書"] } },
  paymentNotice: { title: "{vendor}に渡す仕入明細書", where: "仕入の一覧のタブ、紙の題、メール、確認ページ", suggestions: { genba: ["支払通知書", "支払明細書"], eigyo: ["支払通知書", "支払明細書"] } },
  // 帳票の名前（#266）：言い換えの一覧で変える（12 文字まで）
  deliveryNote: { title: "品物と一緒に{customer}に渡す帳票", where: "メニュー、{deliveryNote}の紙の題、一覧、ホーム", suggestions: { genba: ["納品書", "納品書兼受領書"], eigyo: ["納品書", "出荷案内書", "納品書兼受領書"] } },
  acceptance: { title: "{customer}の受け取り・検査の帳票", where: "{deliveryNote}・請求書に付ける紙の題（検収書の回から）", suggestions: { genba: ["完了報告書", "出来高報告書", "検収書"], eigyo: ["検収書", "物品受領書", "作業報告書", "受領書"] } },
  // 継続案件（#289）：言い換えの一覧で変える（6 文字まで。使う機能「{ongoingProject}」がオンの会社だけ出す）
  ongoingProject: { title: "固定の{customer}の{project}", where: "作成画面の{project}の欄、{project}の一覧のタブと区切り、札", suggestions: { genba: ["継続案件", "現場", "契約", "取引先契約"], eigyo: ["継続案件", "契約", "取引先契約", "現場"] } },
};

// ---------------------------------------------------------------------------
// 明細の列の見出し（#222、2026-10-20 射場さんの決定、docs/22 §6）
//   名称・規格・備考の3つだけ（数量・単位・単価・金額は固定）。会社共通の1組で、設定 → 言葉の言い換え の節「明細の列」で変える（管理者）。
//   1〜6文字・空は不可・3つが同じ言葉にならない（DB の fn_vocabulary_overrides_ok と API が守る）。
//   初期値は業種（会社を作るとき DB の trg_tenants_line_labels が fn_line_labels_default で入れる）。今ある会社は 名称／規格／備考 のまま。
//   紙は出す瞬間に template_snapshot.lineLabels に固める（出したあとで設定を変えても紙は変わらない）。DB の列名・API の識別子（name・spec・note）は変えない
// ---------------------------------------------------------------------------
export const LINE_LABEL_KEYS = ["lineName", "lineSpec", "lineNote"] as const;
export type LineLabelKey = (typeof LINE_LABEL_KEYS)[number];
export const LineLabels = z.object({ lineName: z.string(), lineSpec: z.string(), lineNote: z.string() });
export type LineLabels = z.infer<typeof LineLabels>;
export const DEFAULT_LINE_LABELS: LineLabels = { lineName: "名称", lineSpec: "規格", lineNote: "備考" };
/** 言葉（Labels）から明細の列の3つだけ */
export const lineLabelsOf = (l: Pick<Labels, LineLabelKey>): LineLabels => ({ lineName: l.lineName, lineSpec: l.lineSpec, lineNote: l.lineNote });
export const isLineLabelKey = (k: string): k is LineLabelKey => (LINE_LABEL_KEYS as readonly string[]).includes(k);
/** 帳票の名前（#233・3b）。設定 → {order}「名称」で変える */
export const ORDER_NAME_KEYS = ["order", "orderAck", "paymentNotice"] as const;
/** 支払通知書の名前の候補（設定 → {order}「名称」。#233 3b） */
export const PAYMENT_NOTICE_NAMES = ["支払通知書", "支払明細書"] as const;
export const isOrderNameKey = (k: string): boolean => (ORDER_NAME_KEYS as readonly string[]).includes(k);
/**
 * 言い換えの一覧（設定 → 言葉の言い換え の節「言い換え」）に出すキー＝明細の列と{order}の帳票の名前を除く 19（#266 で deliveryNote）。
 * acceptance（検収書）は 1b（docs/96）から出す（画面は使う機能「{acceptance}」がオンの会社だけ）
 */
export const WORD_KEYS: readonly VocabKey[] = VOCAB_KEYS.filter((k) => !isLineLabelKey(k) && !isOrderNameKey(k));
/**
 * 見積の入力の表（PC・スマホ）の規格の列の見出し。言葉が既定の「規格」なら今までどおり「規格・仕様」（#222 より前の画面のまま。
 * 行の詳細の「外注」と同じ扱い＝axesVendorLabel）。言い換えた会社はその言葉（紙の見出しと同じ）
 */
export const lineSpecOnScreen = (l: Pick<Labels, "lineSpec">): string => (l.lineSpec === DEFAULT_LINE_LABELS.lineSpec ? "規格・仕様" : l.lineSpec);
/** 明細の列の言葉の長さ（紙・画面の今の列の幅に収まる長さ） */
export const LINE_LABEL_MAX = 6;

/**
 * 業種ごとの初期値（#222。DB の fn_line_labels_default と同じ表＝片方を変えたら両方。apps/api/test/line-labels.test.ts が見くらべる）。
 * 物販・卸＝商品名／型番、制作・デザイン・印刷・出版＝品名／仕様、IT・システム・広告・イベント・士業・コンサル＝項目／内容、
 * その他・業種なし・現場力の業種＝名称／規格。備考はどれも「備考」。食品（卸・製造・小売）は単位（#164）と同じく 物販・卸 に合わせた
 */
export function lineLabelsDefault(industry: string | null | undefined): LineLabels {
  switch (industry) {
    case "retail":
    case "food":
      return { lineName: "商品名", lineSpec: "型番", lineNote: "備考" };
    case "design":
    case "printing":
      return { lineName: "品名", lineSpec: "仕様", lineNote: "備考" };
    case "it":
    case "events":
    case "consulting":
      return { lineName: "項目", lineSpec: "内容", lineNote: "備考" };
    default:
      return { ...DEFAULT_LINE_LABELS };
  }
}

/** 明細の列の3つの言葉の誤り（欄ごとの文。空・7文字以上・ほかの列と同じ）。画面の check と API が同じ関数 */
export function lineLabelsErrors(l: LineLabels): Partial<Record<LineLabelKey, string>> {
  const out: Partial<Record<LineLabelKey, string>> = {};
  for (const k of LINE_LABEL_KEYS) {
    const v = l[k].trim();
    if (!v) out[k] = VOCAB_MSG.lineRequired;
    else if ([...v].length > LINE_LABEL_MAX) out[k] = VOCAB_MSG.lineTooLong;
  }
  for (const k of LINE_LABEL_KEYS) {
    if (out[k]) continue;
    const v = l[k].trim();
    if (LINE_LABEL_KEYS.some((o) => o !== k && l[o].trim() === v)) out[k] = VOCAB_MSG.lineDuplicate;
  }
  return out;
}

/** 継続案件の呼び方の長さ（#289。案件欄・一覧の区切り・札に収まる長さ） */
export const ONGOING_WORD_MAX = 6;

/**
 * 継続案件の呼び方の業種の初期値（#289。DB の fn_ongoing_word_default と同じ表＝片方を変えたら両方）。
 * docs/52「言葉の既定は製品で決まる」の例外（明細の列・{acceptance}と同じく業種で初期値）。null＝既定（継続案件）のまま
 */
export function ongoingWordDefault(industry: string | null | undefined): string | null {
  switch (industry) {
    case "wholesale":
      return "現場";
    case "retail":
      return "取引先契約";
    case "it":
    case "consulting":
    case "maintenance":
      return "契約";
    default:
      return null;
  }
}

/**
 * 札・タブ・区切りに出す短い形（#289、Figma ③④）：「継続案件」→「継続」（末尾の「案件」を外す）。現場・契約・取引先契約 はそのまま
 */
export function ongoingShortOf(l: Pick<Labels, "ongoingProject">): string {
  const w = l.ongoingProject.trim();
  const short = w.endsWith("案件") ? w.slice(0, -2) : w;
  return short || w;
}

/** 決まり文の {customer} などを、その会社の言葉に置き換える */
const LABEL_RE = new RegExp(`\\{(${VOCAB_KEYS.join("|")})\\}`, "g");
export function fillLabels(text: string, labels: Labels): string {
  return text.replace(LABEL_RE, (_m, k: VocabKey) => labels[k]);
}

/** GET /company/vocabulary の答え */
export const Vocabulary = z.object({
  preset: VocabularyPreset,
  /** 会社ごとの言い換え（無いキーは既定のまま） */
  overrides: z.partialRecord(VocabKey, z.string()),
  /** 既定＋言い換えを合わせた、いま使う言葉 */
  labels: Labels,
  /** 2つの既定（画面で「工務店向けならこう」と見せる） */
  presets: z.record(VocabularyPreset, Labels),
});
export type Vocabulary = z.infer<typeof Vocabulary>;

export const VOCAB_MSG = {
  tooLong: "言葉は20文字までです",
  /** 明細の列（#222） */
  lineSection: "明細の列",
  lineRequired: "入力してください",
  lineTooLong: "6文字までです",
  lineDuplicate: "ほかの列と同じ言葉は利用できません",
  lineHelp: "見積書・請求書・領収書・見積依頼書の明細表の見出しと、明細の入力の表の見出しです。6文字まで。発行済みの帳票の紙は変わりません。",
} as const;

/** PUT /company/vocabulary。空文字の言い換えは「既定に戻す」（保存しない） */
export const VocabularyUpdate = z.object({
  preset: VocabularyPreset,
  overrides: z.partialRecord(VocabKey, z.string().trim().max(20, VOCAB_MSG.tooLong)).default({}),
});
export type VocabularyUpdate = z.infer<typeof VocabularyUpdate>;
